import { extractDescriptionSearchText, formatSEK, isIncomingSwish } from '@budgie/shared';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { invalidateFinancials } from '../../lib/invalidate';
import { trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { SkeletonLine } from '../common/Skeleton';
import { TransactionCategoryCell } from '../transactions/TransactionCategoryCell';

type Props = {
  year: number;
  month: number;
  categoryId: string | null;
  enabled: boolean;
};

type CategoryTransactionRow = {
  id: string;
  date: string;
  description: string;
  amount: number;
  accountName: string | null;
  isRecurring: boolean | null;
  categorySource: string | null;
  linkedBillId: string | null;
  linkedBillName: string | null;
  linkedLoanId: string | null;
  linkedLoanName: string | null;
};

type SortKey = 'date' | 'amount';

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'date', label: 'Senaste' },
  { key: 'amount', label: 'Störst' },
];

const dayFormat = new Intl.DateTimeFormat('sv-SE', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/** "3 okt" for a YYYY-MM-DD date */
function formatDay(date: string) {
  return dayFormat.format(new Date(`${date}T00:00:00Z`)).replace('.', '');
}

/**
 * The transactions behind one category in a period, as a tappable list. Tapping
 * a row reveals its actions: recategorize, turn into a bill, mark as transfer.
 */
export function CategoryTransactions({ year, month, categoryId, enabled }: Props) {
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [openId, setOpenId] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<CategoryTransactionRow | null>(
    null,
  );
  const [billName, setBillName] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date');

  const query = trpc.insights.categoryTransactions.useQuery(
    { year, month, categoryId },
    { enabled, retry: false },
  );

  const transferMutation = trpc.transaction.setTransferFlag.useMutation({
    onSuccess: async () => {
      await invalidateFinancials(utils);
      toast.success('Transaktionen markerades som intern överföring');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const createBillMutation = trpc.recurringBill.createFromTransaction.useMutation({
    onSuccess: async (result) => {
      await invalidateFinancials(utils);
      setSelectedTransaction(null);
      setBillName('');
      toast.success(
        result.matchedCount > 1
          ? `Räkningen skapades och ${result.matchedCount} transaktioner kopplades.`
          : 'Räkningen skapades.',
      );
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleMarkTransfer = async (transactionId: string) => {
    const ok = await confirm({
      title: 'Markera som intern överföring',
      message: 'Transaktionen räknas då inte längre som inkomst eller utgift.',
      confirmLabel: 'Markera',
    });
    if (!ok) return;
    transferMutation.mutate({ transactionId, isTransfer: true });
  };

  const openCreateBill = (transaction: CategoryTransactionRow) => {
    setSelectedTransaction(transaction);
    setBillName(extractDescriptionSearchText(transaction.description) || transaction.description);
  };

  const closeCreateBill = () => {
    if (createBillMutation.isPending) return;
    setSelectedTransaction(null);
    setBillName('');
  };

  const handleCreateBill = () => {
    if (!selectedTransaction) return;
    const trimmedName = billName.trim();
    if (!trimmedName) {
      toast.error('Ange ett namn för räkningen.');
      return;
    }
    createBillMutation.mutate({ transactionId: selectedTransaction.id, name: trimmedName });
  };

  if (!enabled) return null;

  if (query.isLoading) {
    return (
      <div className="space-y-3 py-1">
        <SkeletonLine width="80%" height="1rem" />
        <SkeletonLine width="65%" height="1rem" />
        <SkeletonLine width="72%" height="1rem" />
      </div>
    );
  }

  if (query.error) {
    return <p className="text-sm text-negative">{query.error.message}</p>;
  }

  const transactions = (query.data as CategoryTransactionRow[] | undefined) ?? [];

  if (transactions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Inga köp i den här kategorin under perioden.</p>
    );
  }

  const sorted =
    sortKey === 'date'
      ? transactions
      : [...transactions].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));

  return (
    <>
      {transactions.length > 1 && (
        <fieldset className="mb-2 flex gap-1">
          <legend className="sr-only">Sortera</legend>
          {SORTS.map((sort) => (
            <button
              key={sort.key}
              type="button"
              aria-pressed={sortKey === sort.key}
              onClick={() => setSortKey(sort.key)}
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium transition-colors max-sm:h-9 max-sm:px-3',
                sortKey === sort.key
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {sort.label}
            </button>
          ))}
        </fieldset>
      )}

      <ul className="divide-y divide-border">
        {sorted.map((transaction) => {
          const isOpen = openId === transaction.id;
          const linkedName = transaction.linkedBillName ?? transaction.linkedLoanName;
          return (
            <li key={transaction.id}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpenId(isOpen ? null : transaction.id)}
                className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-muted/60 sm:gap-3"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{transaction.description}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {formatDay(transaction.date)}
                    {transaction.accountName && ` · ${transaction.accountName}`}
                    {linkedName && ` · ${linkedName}`}
                    {transaction.isRecurring && ' · Återkommande'}
                  </span>
                </span>
                <span
                  className={cn(
                    'shrink-0 text-sm font-medium whitespace-nowrap tabular-nums',
                    transaction.amount > 0 && 'text-positive',
                  )}
                >
                  {transaction.amount > 0 ? '+' : ''}
                  {formatSEK(transaction.amount)}
                </span>
                <ChevronDown
                  className={cn(
                    'size-4 shrink-0 text-muted-foreground transition-transform',
                    isOpen && 'rotate-180',
                  )}
                />
              </button>

              {isOpen && (
                <div className="space-y-3 pb-3 pt-1">
                  <TransactionCategoryCell
                    transactionId={transaction.id}
                    categoryId={categoryId}
                    categorySource={transaction.categorySource}
                    ruleAllowed={!isIncomingSwish(transaction.description, transaction.amount)}
                  />
                  <div className="flex flex-wrap gap-2">
                    {transaction.linkedBillId ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          transaction.linkedBillId &&
                          void navigate({
                            to: '/bills',
                            search: { bill: transaction.linkedBillId },
                          })
                        }
                      >
                        Visa räkning
                      </Button>
                    ) : categoryId !== null && !transaction.linkedLoanId ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={createBillMutation.isPending}
                        onClick={() => openCreateBill(transaction)}
                      >
                        Gör till räkning
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={transferMutation.isPending}
                      onClick={() => void handleMarkTransfer(transaction.id)}
                    >
                      Intern överföring
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <Dialog
        open={selectedTransaction !== null}
        onOpenChange={(open) => !open && closeCreateBill()}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Gör till räkning</DialogTitle>
            <DialogDescription>
              Budgie skapar en regel från köpet och kopplar tidigare köp som matchar.
            </DialogDescription>
          </DialogHeader>

          {selectedTransaction && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3 rounded-xl bg-muted/60 p-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">{selectedTransaction.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDay(selectedTransaction.date)}
                    {' · '}
                    {selectedTransaction.accountName ?? 'Okänt konto'}
                  </p>
                </div>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatSEK(selectedTransaction.amount)}
                </span>
              </div>

              <div>
                <Label htmlFor="bill-name-from-transaction">Namn på räkningen</Label>
                <Input
                  id="bill-name-from-transaction"
                  value={billName}
                  onChange={(event) => setBillName(event.target.value)}
                  className="mt-1"
                  required
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeCreateBill}>
              Avbryt
            </Button>
            <Button
              type="button"
              onClick={handleCreateBill}
              disabled={createBillMutation.isPending}
            >
              {createBillMutation.isPending ? 'Skapar…' : 'Skapa räkning'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
